import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ANALYSIS_RESULTS_START_DELAY_MS,
  DEFAULT_LIMIT,
  SOCKET_TRANSPORTS,
  TASK_SORT_BY,
  VALUE_CHAIN_MAP_ANALYSIS_DATE_FORMAT,
  VALUE_CHAIN_MAP_ANALYSIS_NAME_PREFIX,
  VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES,
  VALUE_CHAIN_MAP_ANALYSIS_RESULTS_POLLING_INTERVAL,
  VALUE_CHAIN_MAP_ANALYSIS_WS_EVENT_RESULT_UPDATED,
} from 'constants/general';
import dayjs from 'dayjs';
import useWebSocket from 'hooks/useWebSocket';
import { useAppDispatch } from 'store/hooks/useApp';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import {
  useCopyValueChainMapAnalysisResultMutation,
  useCopyValueChainMapAnalysisScenariosMutation,
  useCreateValueChainMapAnalysisMutation,
  useCreateValueChainMapAnalysisScenarioMutation,
  useDeleteValueChainMapAnalysisMutation,
  useDeleteValueChainMapAnalysisResultByIdMutation,
  useDeleteValueChainMapAnalysisScenarioMutation,
  useGetValueChainMapAnalysesQuery,
  useGetValueChainMapAnalysisByIdQuery,
  useGetValueChainMapAnalysisResultDownstreamQuery,
  useGetValueChainMapAnalysisResultsQuery,
  useGetValueChainMapAnalysisScenarioDownstreamQuery,
  useLazyGetValueChainMapAnalysisResultsQuery,
  useLazyGetValueChainMapAnalysisScenariosQuery,
  useStartValueChainMapAnalysisMutation,
  useUpdateValueChainMapAnalysisMutation,
  useUpdateValueChainMapAnalysisScenarioMutation,
  valueChainMapApi,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { VALUE_CHAIN_MAP_ANALYSES_TAG } from 'store/tags/valueChainMap';
import { createCountryLookup, getCountryDisplayName } from 'utils/country';

import type { IScenarioFormSubmitData } from 'components/AddScenarioSidebar/interfaces/IAddScenarioSidebar';
import type { IVCAnalysisResultUpdatedPayload } from 'hooks/interfaces/IUseWebSocket';
import type IValueChainMapAnalysisResultItem from 'store/slices/valueChainMap/interfaces/IValueChainMapAnalysisResultItem';
import type IValueChainMapAnalysisScenario from 'store/slices/valueChainMap/interfaces/IValueChainMapAnalysisScenario';
import type {
  IUseValueChainMapAnalysesParams,
  IUseValueChainMapAnalysesReturn,
} from '../interfaces/IUseValueChainMapAnalyses';
import type IValueChainMapAnalysisResultWithOptionalScenarios from '../interfaces/IValueChainMapAnalysisResultWithOptionalScenarios';

const useValueChainMapAnalyses = ({
  valueChainMapId,
  selectedAnalysisId,
}: IUseValueChainMapAnalysesParams): IUseValueChainMapAnalysesReturn => {
  const queryParams = valueChainMapId ? {
    valueChainMapId,
    offset: 0,
    limit: DEFAULT_LIMIT,
    orderBy: TASK_SORT_BY.LAST_MODIFIED,
  } : skipToken;

  const analysisByIdParams = valueChainMapId && selectedAnalysisId != null
    ? { valueChainMapId, id: selectedAnalysisId }
    : skipToken;

  const [isAnalysisResultsQueryEnabled, setIsAnalysisResultsQueryEnabled] = useState(true);
  const [isAnalysisResultsDelayPending, setIsAnalysisResultsDelayPending] = useState(false);
  const analysisResultsDelayTimeoutRef = useRef<number | null>(null);
  const analysisResultsParams = valueChainMapId
    && selectedAnalysisId != null
    && isAnalysisResultsQueryEnabled
    ? {
      valueChainMapId,
      analysisId: selectedAnalysisId,
      offset: 0,
      limit: DEFAULT_LIMIT,
    }
    : skipToken;
  const [activeScenarioId, setActiveScenarioId] = useState<number | null>(null);
  const [activeResultId, setActiveResultId] = useState<number | null>(null);
  const [isAnalysisResultsPolling, setIsAnalysisResultsPolling] = useState(false);
  // The run just started, by the button or by the assistant, kept with its analysis so a change
  // of analysis drops it. Followed until the results list shows it past processing.
  const [followedResult, setFollowedResult] = useState<{
    analysisId: number, resultId: number, view?: boolean,
  } | null>(null);
  const dispatch = useAppDispatch();
  const [analysisResultsPages, setAnalysisResultsPages] = useState<
  Record<number, IValueChainMapAnalysisResultItem[]>
  >({});
  const [analysisResultsTotalCount, setAnalysisResultsTotalCount] = useState(0);
  const [analysisScenariosPages, setAnalysisScenariosPages] = useState<
  Record<number, IValueChainMapAnalysisScenario[]>
  >({});
  const [analysisScenariosTotalCount, setAnalysisScenariosTotalCount] = useState(0);
  const analysisScenarioDownstreamParams = (
    valueChainMapId
    && selectedAnalysisId != null
    && activeScenarioId != null
  )
    ? {
      valueChainMapId,
      analysisId: selectedAnalysisId,
      id: activeScenarioId,
    }
    : skipToken;

  const analysisResultDownstreamParams = (
    valueChainMapId
    && selectedAnalysisId != null
    && activeResultId != null
  )
    ? {
      valueChainMapId,
      analysisId: selectedAnalysisId,
      id: activeResultId,
    }
    : skipToken;

  const { data, isLoading, isFetching } = useGetValueChainMapAnalysesQuery(queryParams);
  const { data: locationsCountries } = useGetLocationsCountriesQuery();
  const {
    data: analysisByIdData,
    isLoading: isAnalysisByIdLoading,
    isFetching: isAnalysisByIdFetching,
  } = useGetValueChainMapAnalysisByIdQuery(analysisByIdParams, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: analysisResultsData,
    isLoading: isAnalysisResultsLoading,
    isFetching: isAnalysisResultsFetching,
  } = useGetValueChainMapAnalysisResultsQuery(
    analysisResultsParams,
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: isAnalysisResultsPolling
        ? VALUE_CHAIN_MAP_ANALYSIS_RESULTS_POLLING_INTERVAL
        : 0,
    },
  );
  const [loadAnalysisResultsPage, { isFetching: isAnalysisResultsPageFetching }] = (
    useLazyGetValueChainMapAnalysisResultsQuery()
  );
  const [loadAnalysisScenariosPage, { isFetching: isAnalysisScenariosLoadingMore }] = (
    useLazyGetValueChainMapAnalysisScenariosQuery()
  );
  const countryLookup = useMemo(
    () => createCountryLookup(locationsCountries?.data),
    [locationsCountries?.data],
  );
  const clearAnalysisResultsDelayTimeout = useCallback(() => {
    if (analysisResultsDelayTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(analysisResultsDelayTimeoutRef.current);
    analysisResultsDelayTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    clearAnalysisResultsDelayTimeout();
    setAnalysisResultsPages({});
    setAnalysisResultsTotalCount(0);
    setIsAnalysisResultsPolling(false);
    setIsAnalysisResultsQueryEnabled(true);
    setIsAnalysisResultsDelayPending(false);
    setActiveResultId(null);
  }, [clearAnalysisResultsDelayTimeout, valueChainMapId, selectedAnalysisId]);

  useEffect(() => () => {
    clearAnalysisResultsDelayTimeout();
  }, [clearAnalysisResultsDelayTimeout]);

  useEffect(() => {
    if (!analysisResultsData?.data) {
      return;
    }

    const { items, meta } = analysisResultsData.data;
    const totalCount = meta.count || 0;

    setAnalysisResultsPages({
      0: items,
    });
    setAnalysisResultsTotalCount(totalCount);
  }, [analysisResultsData]);

  const getHasPendingAnalysisResult = (items: IValueChainMapAnalysisResultItem[]): boolean => (
    items.some(({ status }) => VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES.has(status))
  );

  useEffect(() => {
    if (!analysisResultsData?.data) {
      return;
    }

    const { items } = analysisResultsData.data;
    setIsAnalysisResultsPolling(getHasPendingAnalysisResult(items));
  }, [analysisResultsData]);

  useEffect(() => {
    setFollowedResult((current) => (current && current.analysisId !== selectedAnalysisId ? null : current));
  }, [selectedAnalysisId]);

  // The followed run is done once the list shows it past processing.
  useEffect(() => {
    const items = analysisResultsData?.data?.items;

    if (!items || !followedResult) {
      return;
    }

    const followed = items.find(({ id }) => id === followedResult.resultId);

    if (followed && !VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES.has(followed.status)) {
      if (followedResult.view) {
        setActiveScenarioId(null);
        setActiveResultId(followedResult.resultId);
      }

      setFollowedResult(null);
    }
  }, [analysisResultsData, followedResult]);

  useEffect(() => {
    setAnalysisScenariosPages({});
    setAnalysisScenariosTotalCount(0);
  }, [valueChainMapId]);

  const valueChainMapAnalysisResults = useMemo(() => {
    const seenAnalysisResultIds = new Set<number>();

    return Object.entries(analysisResultsPages)
      .sort(([firstOffset], [secondOffset]) => Number(firstOffset) - Number(secondOffset))
      .flatMap(([, items]) => items)
      .reduce<IValueChainMapAnalysisResultItem[]>((accumulator, item) => {
      if (seenAnalysisResultIds.has(item.id)) {
        return accumulator;
      }

      seenAnalysisResultIds.add(item.id);
      const scenarios = (item as IValueChainMapAnalysisResultWithOptionalScenarios).scenarios ?? [];

      accumulator.push({
        ...item,
        scenarios: scenarios.map((scenario) => ({
          ...scenario,
          unitsSnapshot: (scenario.unitsSnapshot ?? []).map((unit) => ({
            ...unit,
            country: getCountryDisplayName(unit.country, countryLookup),
          })),
        })),
      });

      return accumulator;
    }, [])
      .slice(0, analysisResultsTotalCount);
  }, [analysisResultsPages, analysisResultsTotalCount, countryLookup]);
  const hasMoreAnalysisResults = valueChainMapAnalysisResults.length < analysisResultsTotalCount;
  const valueChainMapAnalysisScenarios = useMemo(() => {
    const seenScenarioIds = new Set<number>();

    return Object.entries(analysisScenariosPages)
      .sort(([firstOffset], [secondOffset]) => Number(firstOffset) - Number(secondOffset))
      .flatMap(([, items]) => items)
      .reduce<IValueChainMapAnalysisScenario[]>((accumulator, item) => {
      if (seenScenarioIds.has(item.id)) {
        return accumulator;
      }

      seenScenarioIds.add(item.id);
      accumulator.push(item);

      return accumulator;
    }, [])
      .slice(0, analysisScenariosTotalCount);
  }, [analysisScenariosPages, analysisScenariosTotalCount]);
  const hasMoreAnalysisScenarios = (
    valueChainMapAnalysisScenarios.length < analysisScenariosTotalCount
  );

  const {
    data: analysisScenarioDownstreamData,
    isLoading: isAnalysisScenarioDownstreamLoading,
    isFetching: isAnalysisScenarioDownstreamFetching,
  } = useGetValueChainMapAnalysisScenarioDownstreamQuery(analysisScenarioDownstreamParams);
  const {
    data: analysisResultDownstreamData,
    isLoading: isAnalysisResultDownstreamLoading,
    isFetching: isAnalysisResultDownstreamFetching,
  } = useGetValueChainMapAnalysisResultDownstreamQuery(analysisResultDownstreamParams);
  const [createAnalysis, { isLoading: isCreateLoading }] = useCreateValueChainMapAnalysisMutation();
  const [deleteAnalysis, { isLoading: isDeleteLoading }] = useDeleteValueChainMapAnalysisMutation();
  const [copyAnalysisResult, { isLoading: isCopyAnalysisResultLoading }] = (
    useCopyValueChainMapAnalysisResultMutation()
  );
  const [copyAnalysisScenarios, { isLoading: isCopyAnalysisScenariosLoading }] = (
    useCopyValueChainMapAnalysisScenariosMutation()
  );
  const [deleteAnalysisResult, { isLoading: isDeleteAnalysisResultLoading }] = (
    useDeleteValueChainMapAnalysisResultByIdMutation()
  );
  const [updateAnalysis, { isLoading: isUpdateLoading }] = useUpdateValueChainMapAnalysisMutation();
  const [startAnalysis, { isLoading: isStartAnalysisLoading }] = useStartValueChainMapAnalysisMutation();
  const [createScenario, { isLoading: isCreateScenarioLoading }] = (
    useCreateValueChainMapAnalysisScenarioMutation()
  );
  const [deleteAnalysisScenario, { isLoading: isDeleteAnalysisScenarioLoading }] = (
    useDeleteValueChainMapAnalysisScenarioMutation()
  );
  const [updateScenario, { isLoading: isUpdateScenarioLoading }] = (
    useUpdateValueChainMapAnalysisScenarioMutation()
  );

  const handleCreateAnalysis = async () => {
    if (!valueChainMapId) return undefined;

    const currentDate = dayjs().format(VALUE_CHAIN_MAP_ANALYSIS_DATE_FORMAT);
    const baseName = `${VALUE_CHAIN_MAP_ANALYSIS_NAME_PREFIX} [${currentDate}]`;
    const existingNames = new Set(
      (data?.data.items || [])
        .map(({ name }) => name)
        .filter((name): name is string => Boolean(name)),
    );

    const analysisName = Array.from(
      { length: existingNames.size + 1 },
      (_, index) => (
        index === 0
          ? baseName
          : `${VALUE_CHAIN_MAP_ANALYSIS_NAME_PREFIX}_${index} [${currentDate}]`
      ),
    ).find((name) => !existingNames.has(name)) ?? baseName;

    const response = await createAnalysis({
      valueChainMapId,
      name: analysisName,
    }).unwrap();

    return response.data;
  };

  const handleDeleteAnalysis = async (scenarioValueChainMapId: number, id: number) => {
    await deleteAnalysis({ valueChainMapId: `${scenarioValueChainMapId}`, id });
  };

  const handleUpdateAnalysis = async (
    scenarioValueChainMapId: number,
    id: number,
    name: string,
  ) => {
    const response = await updateAnalysis({
      valueChainMapId: scenarioValueChainMapId,
      id,
      name,
    }).unwrap();

    return response.data;
  };

  const handleCreateScenario = async (
    analysisId: number,
    formData: IScenarioFormSubmitData,
  ) => {
    if (!valueChainMapId) return;

    const created = await createScenario({
      valueChainMapId,
      analysisId,
      name: formData.name,
      affectedPeriod: formData.affectedPeriod,
      affectedPeriodUnit: formData.affectedPeriodUnit,
      productivityImpact: formData.productivityImpact,
      unitIds: formData.unitIds,
    }).unwrap();

    if (formData.unitIds.length > 0) {
      await updateScenario({
        valueChainMapId,
        analysisId,
        id: created.data.id,
        name: formData.name,
        affectedPeriod: formData.affectedPeriod,
        affectedPeriodUnit: formData.affectedPeriodUnit,
        productivityImpact: formData.productivityImpact,
        unitIds: formData.unitIds,
      }).unwrap();
    }
  };

  const handleStartAnalysis = async (id: number) => {
    if (!valueChainMapId) return;

    clearAnalysisResultsDelayTimeout();
    setIsAnalysisResultsQueryEnabled(false);
    setIsAnalysisResultsDelayPending(true);

    try {
      const response = await startAnalysis({
        valueChainMapId,
        id,
      }).unwrap();

      setAnalysisResultsPages({});
      setAnalysisResultsTotalCount(0);
      setIsAnalysisResultsPolling(false);
      setActiveResultId(null);
      // An API that answers with the run lets the panel follow it by id; an older one leaves the
      // results list to find it.
      setFollowedResult(response?.data?.id != null ? { analysisId: id, resultId: response.data.id } : null);

      analysisResultsDelayTimeoutRef.current = window.setTimeout(() => {
        setIsAnalysisResultsQueryEnabled(true);
        setIsAnalysisResultsDelayPending(false);
        analysisResultsDelayTimeoutRef.current = null;
      }, ANALYSIS_RESULTS_START_DELAY_MS);
    } catch (error) {
      setIsAnalysisResultsQueryEnabled(true);
      setIsAnalysisResultsDelayPending(false);
      throw error;
    }
  };

  /**
   * Follows a run somebody else started — the assistant, through its `startImpactAnalysis` tool —
   * the way `handleStartAnalysis` follows the one it made: the results list is read afresh and
   * kept polling, and the socket listener below is on until the run is past processing.
   *
   * A result the panel already has loaded — "view details" on a run already in the list — skips
   * that reset: the fixed page-0 query args mean it would not actually refetch anything, so all
   * a reset did there was blank the list until the "past processing" effect below quietly reverts
   * `followedResult` to null, and nothing repopulates it. The panel just points at what it already
   * has instead.
   */
  const handleFollowAnalysisResult = useCallback((analysisId: number, resultId: number, view?: boolean) => {
    const isAlreadyLoaded = analysisId === selectedAnalysisId
      && valueChainMapAnalysisResults.some(({ id }) => id === resultId);

    if (!isAlreadyLoaded) {
      clearAnalysisResultsDelayTimeout();
      setAnalysisResultsPages({});
      setAnalysisResultsTotalCount(0);
      setActiveResultId(null);
      setIsAnalysisResultsQueryEnabled(true);
      setIsAnalysisResultsDelayPending(false);
    }

    setFollowedResult({ analysisId, resultId, view });
  }, [clearAnalysisResultsDelayTimeout, selectedAnalysisId, valueChainMapAnalysisResults]);

  // Read at message time, so the listener built once sees the analysis the panel is on now.
  const selectedAnalysisIdRef = useRef(selectedAnalysisId);
  selectedAnalysisIdRef.current = selectedAnalysisId;
  const valueChainMapIdRef = useRef(valueChainMapId);
  valueChainMapIdRef.current = valueChainMapId;

  const handleResultUpdatedSocketMessage = useCallback((event: MessageEvent<string>) => {
    try {
      const payload = JSON.parse(event.data) as IVCAnalysisResultUpdatedPayload;
      const currentValueChainMapId = valueChainMapIdRef.current;
      const currentAnalysisId = selectedAnalysisIdRef.current;

      if (!currentValueChainMapId || payload.analysisId !== currentAnalysisId) {
        return;
      }

      // The event says the run moved; the cache reads where to. The list and the run by id share
      // the tags with the chat's result card, so both views move together whichever listener
      // got the message. Polling stays on as the fallback until the list shows the run done.
      dispatch(valueChainMapApi.util.invalidateTags([
        { type: VALUE_CHAIN_MAP_ANALYSES_TAG, id: `${currentValueChainMapId}-${currentAnalysisId}` },
        {
          type: VALUE_CHAIN_MAP_ANALYSES_TAG,
          id: `${currentValueChainMapId}-${currentAnalysisId}-result-${payload.resultId}`,
        },
      ]));
    } catch {
      // Ignore malformed socket payloads.
    }
  }, [dispatch]);

  // On while a run of the open analysis is in progress, whoever started it; the same socket as
  // the value chain agents use, on its own event.
  useWebSocket({
    event: VALUE_CHAIN_MAP_ANALYSIS_WS_EVENT_RESULT_UPDATED,
    transports: [SOCKET_TRANSPORTS.WEBSOCKET],
    shouldConnect: Boolean(valueChainMapId) && selectedAnalysisId != null
      && (isAnalysisResultsPolling || followedResult != null),
    onMessage: handleResultUpdatedSocketMessage,
  });

  const handleDeleteAnalysisScenario = async (analysisId: number, scenarioId: number) => {
    if (!valueChainMapId) return;

    await deleteAnalysisScenario({
      valueChainMapId,
      analysisId,
      id: scenarioId,
    }).unwrap();
  };

  const handleUpdateScenario = async (
    analysisId: number,
    scenarioId: number,
    formData: IScenarioFormSubmitData,
  ) => {
    if (!valueChainMapId) return;

    await updateScenario({
      valueChainMapId,
      analysisId,
      id: scenarioId,
      name: formData.name,
      affectedPeriod: formData.affectedPeriod,
      affectedPeriodUnit: formData.affectedPeriodUnit,
      productivityImpact: formData.productivityImpact,
      unitIds: formData.unitIds,
    }).unwrap();
  };

  const handleLoadAnalysisResults = async () => {
    if (
      !valueChainMapId
      || selectedAnalysisId == null
      || isAnalysisResultsFetching
      || isAnalysisResultsPageFetching
      || isAnalysisResultsDelayPending
    ) {
      return;
    }

    const response = await loadAnalysisResultsPage({
      valueChainMapId,
      analysisId: selectedAnalysisId,
      offset: 0,
      limit: DEFAULT_LIMIT,
    }).unwrap();
    const { items, meta } = response.data;

    setAnalysisResultsPages({
      0: items,
    });
    setAnalysisResultsTotalCount(meta.count || 0);
    setIsAnalysisResultsPolling(getHasPendingAnalysisResult(items));
  };

  const handleLoadMoreAnalysisResults = async () => {
    if (
      !valueChainMapId
      || selectedAnalysisId == null
      || isAnalysisResultsLoading
      || isAnalysisResultsFetching
      || isAnalysisResultsPageFetching
      || !hasMoreAnalysisResults
    ) {
      return;
    }

    const offset = valueChainMapAnalysisResults.length;
    const response = await loadAnalysisResultsPage({
      valueChainMapId,
      analysisId: selectedAnalysisId,
      offset,
      limit: DEFAULT_LIMIT,
    }).unwrap();

    setAnalysisResultsPages((prevPages) => ({
      ...prevPages,
      [offset]: response.data.items,
    }));
    setAnalysisResultsTotalCount(response.data.meta.count);
  };

  const handleLoadMoreAnalysisScenarios = async () => {
    if (
      !valueChainMapId
      || selectedAnalysisId == null
      || isAnalysisScenariosLoadingMore
      || !hasMoreAnalysisScenarios
    ) {
      return;
    }

    const offset = valueChainMapAnalysisScenarios.length;
    const response = await loadAnalysisScenariosPage({
      valueChainMapId,
      offset,
      limit: DEFAULT_LIMIT,
      orderBy: TASK_SORT_BY.LAST_MODIFIED,
    }).unwrap();

    setAnalysisScenariosPages((prevPages) => ({
      ...prevPages,
      [offset]: response.data.items,
    }));
    setAnalysisScenariosTotalCount(response.data.meta.count);
  };

  const handleLoadAnalysisScenarios = async () => {
    if (!valueChainMapId || selectedAnalysisId == null || isAnalysisScenariosLoadingMore) return;

    setAnalysisScenariosPages({});
    setAnalysisScenariosTotalCount(0);

    const response = await loadAnalysisScenariosPage({
      valueChainMapId,
      offset: 0,
      limit: DEFAULT_LIMIT,
      orderBy: TASK_SORT_BY.LAST_MODIFIED,
    }).unwrap();

    setAnalysisScenariosPages({
      0: response.data.items,
    });
    setAnalysisScenariosTotalCount(response.data.meta.count);
  };

  const handleCopyAnalysisResult = async (resultId: number) => {
    if (!valueChainMapId || selectedAnalysisId == null) return;

    await copyAnalysisResult({
      valueChainMapId,
      analysisId: selectedAnalysisId,
      id: resultId,
    }).unwrap();
  };

  const handleCopyAnalysisScenarios = async (scenarioIds: number[]) => {
    if (!valueChainMapId || selectedAnalysisId == null || scenarioIds.length === 0) return;

    await copyAnalysisScenarios({
      valueChainMapId,
      analysisId: selectedAnalysisId,
      scenarioIds,
    }).unwrap();
  };

  const handleDeleteAnalysisResult = async (resultId: number) => {
    if (!valueChainMapId || selectedAnalysisId == null) return;

    await deleteAnalysisResult({
      valueChainMapId,
      analysisId: selectedAnalysisId,
      id: resultId,
    }).unwrap();
  };

  return {
    analyses: data?.data.items || [],
    isLoading: isLoading || isFetching,
    isCreateLoading,
    isDeleteLoading,
    isUpdateLoading,
    isStartAnalysisLoading,
    isCreateScenarioLoading: isCreateScenarioLoading || isUpdateScenarioLoading,
    isDeleteAnalysisScenarioLoading,
    isCopyAnalysisResultLoading,
    isCopyAnalysisScenariosLoading,
    isDeleteAnalysisResultLoading,
    handleCreateAnalysis,
    handleDeleteAnalysis,
    handleUpdateAnalysis,
    handleStartAnalysis,
    followedResultId: followedResult?.resultId ?? null,
    handleFollowAnalysisResult,
    handleCreateScenario,
    handleUpdateScenario,
    handleDeleteAnalysisScenario,
    handleCopyAnalysisResult,
    handleCopyAnalysisScenarios,
    handleDeleteAnalysisResult,
    valueChainMapAnalysisById: analysisByIdData?.data,
    isAnalysisDetailsLoading: isAnalysisByIdLoading || isAnalysisByIdFetching,
    valueChainMapAnalysisResults,
    isAnalysisResultsLoading: (
      isAnalysisResultsLoading
      || isAnalysisResultsFetching
      || isAnalysisResultsPageFetching
      || isAnalysisResultsDelayPending
    ),
    hasMoreAnalysisResults,
    handleLoadAnalysisResults,
    handleLoadMoreAnalysisResults,
    valueChainMapAnalysisScenarios,
    isAnalysisScenariosLoading: isAnalysisScenariosLoadingMore,
    hasMoreAnalysisScenarios,
    handleLoadAnalysisScenarios,
    handleLoadMoreAnalysisScenarios,
    valueChainMapAnalysisScenarioDownstream: analysisScenarioDownstreamData?.data,
    isAnalysisScenarioDownstreamLoading: (
      isAnalysisScenarioDownstreamLoading
      || isAnalysisScenarioDownstreamFetching
    ),
    activeScenarioId,
    handleSetActiveScenarioId: setActiveScenarioId,
    valueChainMapAnalysisResultDownstream: analysisResultDownstreamData?.data,
    isAnalysisResultDownstreamLoading: (
      isAnalysisResultDownstreamLoading
      || isAnalysisResultDownstreamFetching
    ),
    activeResultId,
    handleSetActiveResultId: setActiveResultId,
  };
};

export default useValueChainMapAnalyses;
